import { Component, Input } from '@angular/core';
import { ConfigProvider } from "../../../config-provider.service";
import { CurrentUser } from '../../providers/current-user';
import { SimpleDataSource } from '../../providers/simple-data-source';
import { UtilsProvider } from "../../providers/utils-provider.service";
import { DecimalPipe, NgForOf, NgIf } from "@angular/common";
import {
  MatCell,
  MatCellDef,
  MatColumnDef, MatHeaderCell,
  MatHeaderCellDef, MatHeaderRow,
  MatHeaderRowDef, MatRow,
  MatRowDef,
  MatTable
} from "@angular/material/table";
import { MatTooltip } from "@angular/material/tooltip";
import { MatIcon } from "@angular/material/icon";
import { TranslateModule } from "@ngx-translate/core";

@Component({
  selector: 'table-histogram-chart',
  providers: [],
  styleUrls: ['./table-histogram-chart.scss'],
  standalone: true,
  imports: [
    NgForOf,
    NgIf,
    MatCellDef,
    MatRowDef,
    MatHeaderRowDef,
    MatHeaderCellDef,
    MatColumnDef,
    MatTable,
    MatTooltip,
    MatRow,
    MatHeaderRow,
    MatCell,
    MatHeaderCell,
    MatIcon,
    DecimalPipe,
    TranslateModule,
  ],
  templateUrl: './table-histogram-chart.html'
})

export class TableHistogramChartComponent {
    @Input() public set rows(rows: TableHistogramRow[]) {
        this._rows = rows;
        this.updateDataSource();
    }
    public get rows() {
        return this._rows;
    }
    @Input() public set columns(columns: TableHistogramColumn[]) {
        this._columns = columns;
        this.updateDataSource();
    }
    public get columns() {
        return this._columns;
    }
    @Input() public set values(values: TableHistogramValue[]) {
        this._values = values;
        this.updateDataSource();
    }
    public get values() {
        return this._values;
    }
    @Input() public emptyMessage = 'No_data';
    @Input() public hasImage = false;
    @Input() public showOnlyBar = false;
    @Input() public isImageCircle = false;
    @Input() public imageFallbackIcon: string | null = null;
    @Input() public valueColumnName = 'Value';

    public tableColumns: string[] = ['image', 'title', 'value', 'chart'];
    public dataSource: SimpleDataSource = new SimpleDataSource([]);
    public rawData: TableHistogramRowComputed[] = [];

    private _rows: TableHistogramRow[] = [];
    private _columns: TableHistogramColumn[] = [];
    private _values: TableHistogramValue[] = [];

    constructor(
        public conf: ConfigProvider,
        public utils: UtilsProvider,
        public currentUser: CurrentUser
    ) {
    }

    private updateDataSource(): void {
        if (!this._rows || this._rows.length === 0
            || !this._columns || this._columns.length === 0
            || !this._values || this._values.length === 0) {
            this.dataSource = new SimpleDataSource([]);

            return;
        }

        const data: TableHistogramRowComputed[] = [];
        let max = 0;
        for (let i = 0; i < this._rows.length; i++) {
            let total = 0;
            const columns: TableHistogramColumnComputed[] = [];
            for (let j = 0; j < this._columns.length; j++) {
                const value: TableHistogramValue = this.buildValue(this._rows[i].id, this._columns[j].id);
                total += value.value;
                columns.push({
                    column: this._columns[j],
                    value,
                    share: 0
                });
            }

            for (let j = 0; j < columns.length; j++) {
                columns[j].share = columns[j].value.value / total * 100;
            }

            max = total > max ? total : max;

            data.push({
                row: this._rows[i],
                columns,
                value: total * 10,
                share: 0
            });
        }

        for (let i = 0; i < data.length; i++) {
            data[i].share = data[i].value / max * 100;
        }

        this.dataSource = new SimpleDataSource(data);
        this.rawData = data;

    }

    private buildValue(rowId: number, columnId: number): TableHistogramValue {
        let total = 0;
        for (let i = 0; i < this._values.length; i++) {
            if (this._values[i].rowId === rowId && this._values[i].columnId === columnId) {
                total += this._values[i].value;
            }
        }

        return {
            rowId,
            columnId,
            value: total,
            item: null
        };
    }
}

export interface TableHistogramRow {
    id: number;
    image: string;
    title: string;
    subTitle: string;
}

export interface TableHistogramColumn {
    id: number;
    title: string;
    color: string;
}

export interface TableHistogramValue {
    rowId: number;
    columnId: number;
    value: number;
    item: any;
}

interface TableHistogramRowComputed {
    row: TableHistogramRow;
    columns: TableHistogramColumnComputed[];
    value: number;
    share: number;
}

interface TableHistogramColumnComputed {
    column: TableHistogramColumn;
    value: TableHistogramValue;
    share: number;
}
